import { Virtualizer } from '../../../../components/ui-components/Virtualizer'
import type { WithdrawTransaction, HarvestTypeFilter } from '../../types/withdrawals.types'
import { TransactionCard } from './TransactionCard'
import { WithdrawalsEmptyState } from './WithdrawalsEmptyState'
import { getBottomPadding } from '../../../../utils/sessionStorage'

const TRANSACTION_CARD_BASE_HEIGHT = 116
const TRANSACTION_CARD_GAINS_LINE_HEIGHT = 21.5

function countCapitalGainsLines(info: WithdrawTransaction['financialYearCapitalGains']): number {
  if (!info) return 0
  let n = 0
  if ((info?.ltcg ?? 0) !== 0) n++
  if ((info?.stcg ?? 0) !== 0) n++
  if ((info?.ltcl ?? 0) !== 0) n++
  if ((info?.stcl ?? 0) !== 0) n++
  return n
}

function getTransactionCardEstimateHeight(transaction: WithdrawTransaction): number {
  const lines = countCapitalGainsLines(transaction?.financialYearCapitalGains)
  return TRANSACTION_CARD_BASE_HEIGHT + (TRANSACTION_CARD_GAINS_LINE_HEIGHT * lines)
}

/**
 * Virtualizer key that forces a remount when scroll mode, layout offset, or filter changes.
 * TanStack Virtual caches item sizes by index. When the filter changes we get a new filteredList
 * but the same instance would keep old cached heights, so total size and positions stay wrong.
 * Changing this key remounts the Virtualizer so it recalculates all heights for the current list.
 */
function getWithdrawalsVirtualizerKey(
  useExternalScroll: boolean,
  paddingStart: number,
  activeFilters: HarvestTypeFilter[]
): string {
  const scrollPart = useExternalScroll ? 'ext' : 'int'
  return `${scrollPart}-${paddingStart}-${activeFilters.join(',')}`
}

type WithdrawalsTransactionsSectionProps = {
  scrollElementRef?: React.RefObject<HTMLDivElement | null>
  paddingStart?: number
  activeFilters: HarvestTypeFilter[]
  filteredList: WithdrawTransaction[]
  isEmptyList: boolean
  isEmptyFiltered: boolean
}

export function WithdrawalsTransactionsSection({
  scrollElementRef,
  paddingStart = 0,
  activeFilters,
  filteredList,
  isEmptyList,
  isEmptyFiltered,
}: WithdrawalsTransactionsSectionProps) {
  const useExternalScroll = scrollElementRef != null

  return (
    <section
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      className={`w-full ${useExternalScroll ? 'pt-4' : 'flex-1 min-h-0 flex flex-col overflow-hidden'}`}
    >
      {isEmptyList && (
        <WithdrawalsEmptyState variant="no_transactions_yet" />
      )}

      {isEmptyFiltered && (
        <WithdrawalsEmptyState variant="no_transactions_found" />
      )}

      {filteredList.length > 0 && (
        <Virtualizer<WithdrawTransaction>
          key={getWithdrawalsVirtualizerKey(useExternalScroll, paddingStart, activeFilters)}
          scrollElementRef={scrollElementRef}
          paddingStart={paddingStart}
          items={filteredList}
          listSemantics
          estimateSize={(index) =>
            getTransactionCardEstimateHeight(filteredList[index] ?? ({} as WithdrawTransaction))
          }
          overscan={5}
          gap={8}
          className={useExternalScroll ? 'pb-2' : 'h-full pb-2'}
          itemClassName=""
          getItemKey={(item, index) => item?.isin + index + item?.transactionDate}
        >
          {(item, index) => <TransactionCard transaction={item} isLast={index === filteredList.length - 1} />}
        </Virtualizer>
      )}
    </section>
  )
}
